import { AdminTableContainer, Badge, Checkbox, cn, Label } from "@simplicity/ui";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import type { RunStatus } from "../types";
import { RunsTruncatedNotice } from "./RunsTruncatedNotice";
import { TypeBadge } from "./TypeBadge";

// Summary types — connector runs without tableRuns, used by the lightweight polling endpoint
export interface ConnectorRunSummary {
  id: number;
  connectorId: string;
  connector: { id: string; name: string; type: string; prefix: string };
  startedAt: string;
  endedAt: string | null;
  status: RunStatus;
}

export interface ProjectRunSummary {
  id: number;
  projectId: string;
  project: { id: string; name: string };
  scriptId?: string | null;
  functionName?: string | null;
  runEventSampleRate: number;
  startedAt: string;
  endedAt: string | null;
  status: RunStatus;
  connectorRuns: ConnectorRunSummary[];
}

export interface RunsSummaryResponse {
  runs: ProjectRunSummary[];
  startDate: string;
  endDate: string;
  // Absent from a server that predates the field (SIM-2874).
  truncated?: boolean;
}

type ConnectorRun = ConnectorRunSummary;

function toDateParam(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function getDateKey(dateInput: string | Date): string {
  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getLastNDays(n: number): Date[] {
  const days: Date[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = n - 1; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    days.push(date);
  }
  return days;
}

type DisplayStatus = RunStatus | "none" | "partial_failure";

interface StatusIconProps {
  status: DisplayStatus;
  count?: number;
  title?: string;
  linkTo?: string;
}

const STATUS_CONFIG: Record<
  DisplayStatus,
  {
    icon: string;
    variant: "success" | "info" | "secondary" | "destructive" | "warning";
    pulse?: boolean;
  }
> = {
  completed: { icon: "✓", variant: "success" },
  continued: { icon: "→", variant: "info" },
  started: {
    icon: "●",
    variant: "info",
    pulse: true,
  },
  failed: { icon: "✕", variant: "destructive" },
  timed_out: { icon: "⏱", variant: "warning" },
  abandoned: { icon: "⊘", variant: "warning" },
  partial_failure: { icon: "⚠", variant: "warning" },
  none: { icon: "–", variant: "secondary" },
};

function StatusIcon({ status, count, title, linkTo }: StatusIconProps) {
  const { icon, variant, pulse } = STATUS_CONFIG[status];
  const isLinked = linkTo && status !== "none";

  if (status === "none") {
    return <span className="text-[0.8rem] leading-none text-muted-foreground">–</span>;
  }

  const badge = (
    <Badge
      variant={variant}
      className={cn(
        "inline-flex items-center gap-1 px-[0.35rem] py-[0.15rem] rounded-[6px] whitespace-pre-wrap transition-transform duration-100 cursor-default",
        isLinked &&
          "cursor-pointer group-hover:scale-[1.15] group-hover:ring-2 group-hover:ring-current/40",
        pulse && "badge-pulse",
      )}
      title={title}
    >
      <span className="text-[0.8rem] leading-none">{icon}</span>
      {count !== undefined && <span className="text-xs font-semibold leading-none">{count}</span>}
    </Badge>
  );

  if (isLinked) {
    return (
      <Link to={linkTo} className="group inline-block no-underline">
        {badge}
      </Link>
    );
  }

  return badge;
}

function getDisplayStatus<T extends { status: RunStatus }>(runs: T[]): DisplayStatus {
  if (runs.length === 0) return "none";
  const latestStatus = runs[0].status;
  if (latestStatus !== "completed") return latestStatus;
  const hasFailure = runs.some(
    (run) => run.status === "failed" || run.status === "timed_out" || run.status === "abandoned",
  );
  return hasFailure ? "partial_failure" : "completed";
}

function generateRunsTooltip<
  T extends { startedAt: string; endedAt: string | null; status: RunStatus },
>(runs: T[], label: string): string {
  if (runs.length === 0) return "No runs";
  const lines = runs.map((run, i) => {
    const start = formatTime(run.startedAt);
    const end = run.endedAt ? formatTime(run.endedAt) : "running";
    return `${i + 1}. ${start} → ${end} (${run.status})`;
  });
  return `${label}\n${lines.join("\n")}`;
}

type RunsByDate<T> = Map<string, T[]>;

type OverviewRow = {
  id: string;
  projectId: string;
  projectName: string;
  connectorType: string;
  functionName: string;
  functionNames: string[];
  runsByDate: RunsByDate<ConnectorRun>;
};

function OverviewRowItem({
  row,
  dates,
  showProjectName,
  onRowClick,
  showFunction,
}: {
  row: OverviewRow;
  dates: Date[];
  showProjectName: boolean;
  onRowClick: (row: OverviewRow) => void;
  showFunction: boolean;
}) {
  return (
    // eslint-disable-next-line jsx-a11y/prefer-tag-over-role -- <tr> stays a table-row; semantic <button> can't replace it without breaking table structure. Whole-row click target with keyboard support; nested cells contain their own interactive content.
    <tr
      className="row-connector"
      role="button"
      tabIndex={0}
      onClick={() => onRowClick(row)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onRowClick(row);
        }
      }}
    >
      <td className="cell-project">{showProjectName ? row.projectName : ""}</td>
      <td className="cell-connector">
        <TypeBadge type={row.connectorType} />
      </td>
      {showFunction ? <td className="cell-function">{row.functionName}</td> : null}
      {dates.map((date) => {
        const dateKey = getDateKey(date);
        const runs = row.runsByDate.get(dateKey) ?? [];
        const displayStatus = getDisplayStatus(runs);
        const tooltip = generateRunsTooltip(
          runs,
          `${row.projectName} - ${row.connectorType} - ${row.functionName} - ${runs.length} run(s)`,
        );
        const linkTo = `/detail?projectId=${row.projectId}&date=${toDateParam(date)}`;
        return (
          <td key={dateKey} className="cell-status" onClick={(e) => e.stopPropagation()}>
            <StatusIcon status={displayStatus} title={tooltip} linkTo={linkTo} />
          </td>
        );
      })}
    </tr>
  );
}

interface RunsReportProps {
  data: RunsSummaryResponse | null;
  loading: boolean;
  error: string | null;
}

function getOrCreateRow(
  rows: Map<string, OverviewRow>,
  rowKey: string,
  run: ProjectRunSummary,
  connectorRun: ConnectorRun,
): OverviewRow {
  let row = rows.get(rowKey);
  if (!row) {
    row = {
      id: rowKey,
      projectId: run.projectId,
      projectName: run.project.name,
      connectorType: connectorRun.connector.type,
      functionName: "",
      functionNames: [],
      runsByDate: new Map(),
    };
    rows.set(rowKey, row);
  }
  return row;
}

function addConnectorRunToRow(row: OverviewRow, connectorRun: ConnectorRun, functionName: string) {
  if (functionName && !row.functionNames.includes(functionName)) {
    row.functionNames.push(functionName);
  }
  const dateKey = getDateKey(connectorRun.startedAt);
  let dateRuns = row.runsByDate.get(dateKey);
  if (!dateRuns) {
    dateRuns = [];
    row.runsByDate.set(dateKey, dateRuns);
  }
  dateRuns.push(connectorRun);
}

function resolveFunctionName(row: OverviewRow, splitByFunction: boolean) {
  if (splitByFunction) {
    row.functionName = row.functionNames[0] ?? "";
    return;
  }
  if (row.functionNames.length === 0) row.functionName = "";
  else if (row.functionNames.length === 1) row.functionName = row.functionNames[0];
  else row.functionName = "Multiple";
}

function sortOverviewRows(rows: OverviewRow[]): OverviewRow[] {
  return rows.sort((a, b) => {
    const projectCompare = a.projectName.localeCompare(b.projectName);
    if (projectCompare !== 0) return projectCompare;
    const connectorCompare = a.connectorType.localeCompare(b.connectorType);
    if (connectorCompare !== 0) return connectorCompare;
    const aEmpty = a.functionName.trim().length === 0;
    const bEmpty = b.functionName.trim().length === 0;
    if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
    return a.functionName.localeCompare(b.functionName);
  });
}

function computeOverviewRows(
  data: RunsSummaryResponse | null,
  splitByFunction: boolean,
): OverviewRow[] {
  if (!data) return [];
  const rows = new Map<string, OverviewRow>();
  const sortedRuns = [...data.runs].sort(
    (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
  );

  for (const run of sortedRuns) {
    const functionName =
      run.functionName && run.functionName.trim().length > 0 ? run.functionName : "";
    const sortedConnectorRuns = [...run.connectorRuns].sort(
      (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
    );
    for (const connectorRun of sortedConnectorRuns) {
      const rowKey = splitByFunction
        ? [run.projectId, connectorRun.connector.type, functionName].join("|")
        : [run.projectId, connectorRun.connector.type].join("|");
      const row = getOrCreateRow(rows, rowKey, run, connectorRun);
      addConnectorRunToRow(row, connectorRun, functionName);
    }
  }

  for (const row of rows.values()) resolveFunctionName(row, splitByFunction);
  return sortOverviewRows(Array.from(rows.values()));
}

function RunsReportLegend({
  splitByFunction,
  setSplitByFunction,
}: {
  splitByFunction: boolean;
  setSplitByFunction: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-4">
      <Label className="mr-3 inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-muted-foreground">
        <Checkbox
          checked={splitByFunction}
          onCheckedChange={(v) => setSplitByFunction(v === true)}
        />
        Split by function
      </Label>
      <div className="flex gap-6 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <StatusIcon status="completed" /> Completed
        </span>
        <span className="flex items-center gap-1.5">
          <StatusIcon status="continued" /> Continued
        </span>
        <span className="flex items-center gap-1.5">
          <StatusIcon status="partial_failure" /> Partial Failure
        </span>
        <span className="flex items-center gap-1.5">
          <StatusIcon status="started" /> Running
        </span>
        <span className="flex items-center gap-1.5">
          <StatusIcon status="failed" /> Failed
        </span>
        <span className="flex items-center gap-1.5">
          <StatusIcon status="timed_out" /> Timed Out
        </span>
        <span className="flex items-center gap-1.5">
          <StatusIcon status="abandoned" /> Abandoned
        </span>
      </div>
    </div>
  );
}

function groupByProject(rows: OverviewRow[]): Map<string, OverviewRow[]> {
  const grouped = new Map<string, OverviewRow[]>();
  for (const row of rows) {
    let group = grouped.get(row.projectName);
    if (!group) {
      group = [];
      grouped.set(row.projectName, group);
    }
    group.push(row);
  }
  return grouped;
}

function RunsReportTableBody({
  overviewRows,
  dates,
  splitByFunction,
  onRowClick,
}: {
  overviewRows: OverviewRow[];
  dates: Date[];
  splitByFunction: boolean;
  onRowClick: (row: OverviewRow) => void;
}) {
  if (overviewRows.length === 0) {
    return (
      <tbody>
        <tr>
          <td colSpan={dates.length + (splitByFunction ? 3 : 2)} className="no-data">
            No runs found in the last 14 days
          </td>
        </tr>
      </tbody>
    );
  }
  const grouped = groupByProject(overviewRows);
  return (
    <>
      {Array.from(grouped.entries()).map(([projectName, rows]) => (
        <tbody key={projectName}>
          {rows.map((row, index) => (
            <OverviewRowItem
              key={row.id}
              row={row}
              dates={dates}
              showProjectName={index === 0}
              showFunction={splitByFunction}
              onRowClick={onRowClick}
            />
          ))}
        </tbody>
      ))}
    </>
  );
}

export function RunsReport({ data, loading, error }: RunsReportProps) {
  const navigate = useNavigate();
  const dates = useMemo(() => getLastNDays(14), []);
  const [splitByFunction, setSplitByFunction] = useState<boolean>(false);

  const overviewRows = useMemo(
    () => computeOverviewRows(data, splitByFunction),
    [data, splitByFunction],
  );

  if (loading) {
    return (
      <div className="page-view empty-state max-w-[1400px] font-mono">
        <div className="dv-spinner" />
        <p>Loading runs report...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-view empty-state max-w-[1400px] font-mono">
        <p>⚠️ {error}</p>
      </div>
    );
  }

  return (
    <div className="page-view max-w-[1400px] font-mono">
      <div className="section-header">
        <h2 className="text-2xl">Runs Report</h2>
        <RunsReportLegend
          splitByFunction={splitByFunction}
          setSplitByFunction={setSplitByFunction}
        />
      </div>
      <RunsTruncatedNotice data={data} />

      <AdminTableContainer aria-label="Runs report">
        <table className="admin-table pivot-table">
          <thead>
            <tr>
              <th className="cell-project">Project</th>
              <th className="cell-connector">Connector</th>
              {splitByFunction ? <th className="cell-function">Function</th> : null}
              {dates.map((date) => (
                <th key={date.toISOString()} className="th-date">
                  <span className="date-label">{formatDate(date)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <RunsReportTableBody
            overviewRows={overviewRows}
            dates={dates}
            splitByFunction={splitByFunction}
            onRowClick={(clickedRow) => void navigate(`/detail?projectId=${clickedRow.projectId}`)}
          />
        </table>
      </AdminTableContainer>
    </div>
  );
}
